/**
 * API Service for Car Pulse Tracker (v2: connect-first)
 *
 * Backend implementation: ${ORG}-${APP}-api
 * Backend runs on: http://localhost:8100 (default)
 * API prefix: /api/v1
 *
 * Every request routes through the shared `HttpClient` wrapper, which
 * centralizes header construction (Content-Type, trace, auth token), JSON
 * (de)serialisation, and uniform error shaping via `ApiError`.
 */
import type { VehicleReport, SessionVehicle, SessionVehiclesResponse, CatalogResponse, QuoteResponse } from '@/types';
import { appConfig } from '@/config';
import { createHttpClient } from '@/services/httpClient';

const http = createHttpClient(appConfig.apiBaseUrl);

export interface VehicleReportOutcome {
  vin: string;
  provider: string;
  core_status: string;
  reportability: string;
  failure_reason?: string | null;
  modules: Record<string, {
    status: string;
    source_auth?: string | null;
    error_code?: string | null;
    error_detail?: string | null;
    http_status?: number | null;
  }>;
}

// ── Tesla OAuth ────────────────────────────────────────────────────

export async function initiateTeslaOAuth(brand: string): Promise<string> {
  const data = await http.post<{ authUrl: string }>('/tesla/oauth/initiate', { brand }, {
    errorMessage: 'OAuth initiation failed',
    useErrorDetail: false,
  });
  return data.authUrl;
}

// ── Session Endpoints ──────────────────────────────────────────────

export async function getSessionVehicles(sessionId: string): Promise<SessionVehiclesResponse> {
  return http.get<SessionVehiclesResponse>(`/tesla/session/${sessionId}/vehicles`, {
    errorMessage: 'Failed to fetch session vehicles',
    useErrorDetail: false,
  });
}

export async function getSessionState(sessionId: string): Promise<{
  session_id: string;
  status: string;
  step: string;
  expires_at?: string | null;
  selected_vehicle_ids: number[];
  selected_package: string | null;
  payment_id: string | null;
  payment_method: string | null;
  vehicles: SessionVehicle[];
  reports: Record<string, any>;
  outcomes?: Record<string, VehicleReportOutcome>;
}> {
  return http.get(`/tesla/session/${sessionId}/state`, {
    errorMessage: 'Failed to fetch session state',
    useErrorDetail: false,
  });
}

export async function selectVehiclesAndPackage(
  sessionId: string,
  vehicleIds: number[],
  pkg: string
): Promise<{ status: string; vehicle_count: number; package: string }> {
  return http.post(`/tesla/session/${sessionId}/select`, { vehicle_ids: vehicleIds, package: pkg }, {
    errorMessage: 'Failed to select vehicles',
  });
}

// ── Payment ────────────────────────────────────────────────────────

export async function createPaymentIntent(
  sessionId: string,
  paymentMethod: 'stripe' = 'stripe'
): Promise<{ clientSecret: string; paymentIntentId: string; redirectUrl?: string }> {
  const data = await http.post<{ client_secret?: string; payment_id: string; redirect_url?: string }>(
    '/payment/create-intent',
    { session_id: sessionId, payment_method: paymentMethod },
    { errorMessage: 'Failed to create payment intent' }
  );
  return {
    clientSecret: data.client_secret || '',
    paymentIntentId: data.payment_id,
    redirectUrl: data.redirect_url || undefined,
  };
}

export async function verifyPayment(paymentId: string): Promise<{ status: string; plan: string }> {
  const data = await http.post<{ status: string; plan: string }>('/payment/verify', { payment_id: paymentId }, {
    errorMessage: 'Failed to verify payment',
  });
  return { status: data.status, plan: data.plan };
}

// ── Pricing / CPQ ────────────────────────────────────────────────

export async function fetchCatalog(): Promise<CatalogResponse> {
  return http.get<CatalogResponse>('/pricing/catalog', {
    errorMessage: 'Failed to fetch pricing catalog',
    useErrorDetail: false,
  });
}

export async function fetchQuote(
  sessionId: string
): Promise<QuoteResponse> {
  const body: Record<string, string> = { session_id: sessionId };
  return http.post<QuoteResponse>('/pricing/quote', body, {
    errorMessage: 'Failed to compute quote',
  });
}

// ── Reports ────────────────────────────────────────────────────────

export async function generateReports(sessionId: string, lang?: string): Promise<{ status: string; reports: Record<string, any>; outcomes?: Record<string, VehicleReportOutcome>; retry_count?: number; error?: string }> {
  return http.post('/tesla/reports/generate', { session_id: sessionId, lang }, {
    errorMessage: 'Failed to generate reports',
  });
}

export async function getSessionReports(sessionId: string): Promise<{ status: string; reports: Record<string, any>; outcomes?: Record<string, VehicleReportOutcome>; retry_count?: number; error?: string }> {
  return http.get(`/tesla/report/${sessionId}`, {
    errorMessage: 'Failed to fetch reports',
  });
}

export async function generatePdfReport(vehicleReport: VehicleReport, lang?: string, vehiclePhotos?: string[], sessionId?: string): Promise<Blob> {
  return http.post<Blob>('/tesla/vehicle-report/pdf', { vehicleReport, lang, vehiclePhotos: vehiclePhotos || [], session_id: sessionId }, {
    errorMessage: 'Failed to generate PDF report',
    responseType: 'blob',
    skipUnauthorizedHandler: true,
  });
}

export async function generatePdfReportAsync(
  vehicleReport: VehicleReport,
  lang?: string,
  vehiclePhotos?: string[],
  sessionId?: string
): Promise<{ task_id: string; status: string }> {
  return http.post('/tesla/vehicle-report/pdf/async', { vehicleReport, lang, vehiclePhotos: vehiclePhotos || [], session_id: sessionId }, {
    errorMessage: 'Failed to initiate async PDF report',
    skipUnauthorizedHandler: true,
  });
}

export async function getReportTaskStatus(taskId: string, sessionId: string): Promise<{
  status: 'processing' | 'complete' | 'failed';
  download_url?: string;
  report_id?: string;
  error?: string;
}> {
  return http.get(`/tesla/reports/status/${taskId}?session_id=${encodeURIComponent(sessionId)}`, {
    errorMessage: 'Failed to fetch task status',
    useErrorDetail: false,
    skipUnauthorizedHandler: true,
  });
}

export function getChargingInvoiceDownloadUrl(orderId: string, vin: string, invoiceId: string): string {
  return `${appConfig.apiBaseUrl}/tesla/orders/${orderId}/vehicles/${encodeURIComponent(vin)}/charging-invoices/${encodeURIComponent(invoiceId)}`;
}

export function getChargingInvoiceZipUrl(orderId: string, vin: string, months = 24): string {
  return `${appConfig.apiBaseUrl}/tesla/orders/${orderId}/vehicles/${encodeURIComponent(vin)}/charging-invoices.zip?months=${months}`;
}

export function getSessionInvoiceZipUrl(sessionId: string, months = 24): string {
  return `${appConfig.apiBaseUrl}/tesla/session/${encodeURIComponent(sessionId)}/charging-invoices.zip?months=${months}`;
}

export function getChargingCsvUrl(orderId: string, vin: string, months = 24): string {
  return `${appConfig.apiBaseUrl}/tesla/orders/${orderId}/vehicles/${encodeURIComponent(vin)}/charging-history.csv?months=${months}`;
}

// ── Payment Receipt ──────────────────────────────────────────────

export async function downloadReceiptPdf(paymentId: string): Promise<Blob> {
  return http.get<Blob>(`/payment/receipt/${encodeURIComponent(paymentId)}/pdf`, {
    errorMessage: 'Failed to download receipt',
    responseType: 'blob',
    skipUnauthorizedHandler: true,
  });
}


// ── Cleanup (Trace-On feature) ──────────────────────────────────

export async function deleteReport(reportId: string): Promise<{ status: string }> {
  return http.delete(`/tesla/reports/${encodeURIComponent(reportId)}`, {
    errorMessage: 'Failed to delete report',
  });
}

export async function deleteTraces(sessionId: string): Promise<{ status: string; deleted_count: number }> {
  return http.delete(`/tesla/traces/${encodeURIComponent(sessionId)}`, {
    errorMessage: 'Failed to delete traces',
  });
}

// ── Session PDF ZIP export (spec 054) ─────────────────────────────

export type SessionPdfZipStatusValue = 'queued' | 'processing' | 'complete' | 'failed';

export interface SessionPdfZipInclude {
  reports?: boolean;
  invoices?: boolean;
  receipt?: boolean;
}

export interface CreateSessionPdfZipRequest {
  months?: number;
  lang?: string;
  include?: SessionPdfZipInclude;
  payment_id?: string | null;
  /** VIN -> base64 data-URL photos so ZIP report PDFs match single Save PDF */
  vehiclePhotos?: Record<string, string[]>;
}

export interface SessionPdfZipManifestSummary {
  counts?: {
    reports?: number;
    invoices?: number;
    receipts?: number;
    skipped?: number;
  };
  [key: string]: unknown;
}

export interface SessionPdfZipExportResponse {
  export_id: string;
  status: SessionPdfZipStatusValue;
  progress: number;
  months: number;
  poll_after_ms?: number;
  filename?: string | null;
  download_url?: string | null;
  manifest_summary?: SessionPdfZipManifestSummary | null;
  error?: string | null;
}

/** Alias used by useSessionPdfZip composable */
export type SessionPdfZipStatus = SessionPdfZipExportResponse;


/**
 * Create (or resume) an async session PDF package job.
 * Returns 202/200 body: export_id, status, progress, months, poll_after_ms.
 */
export async function createSessionPdfZipExport(
  sessionId: string,
  body: CreateSessionPdfZipRequest = {}
): Promise<SessionPdfZipExportResponse> {
  return http.post<SessionPdfZipExportResponse>(
    `/tesla/session/${encodeURIComponent(sessionId)}/exports/pdf-zip`,
    body,
    {
      errorMessage: 'Failed to create session PDF ZIP export',
      skipUnauthorizedHandler: true,
    }
  );
}

/**
 * Poll status of a session PDF ZIP export job.
 */
export async function getSessionPdfZipStatus(
  sessionId: string,
  exportId: string
): Promise<SessionPdfZipExportResponse> {
  return http.get<SessionPdfZipExportResponse>(
    `/tesla/session/${encodeURIComponent(sessionId)}/exports/${encodeURIComponent(exportId)}`,
    {
      errorMessage: 'Failed to fetch session PDF ZIP status',
      useErrorDetail: false,
      skipUnauthorizedHandler: true,
    }
  );
}

/**
 * Download ZIP bytes when status is complete (fallback when signed URL unavailable).
 */
export async function downloadSessionPdfZipFile(
  sessionId: string,
  exportId: string
): Promise<Blob> {
  return http.get<Blob>(
    `/tesla/session/${encodeURIComponent(sessionId)}/exports/${encodeURIComponent(exportId)}/file`,
    {
      errorMessage: 'Failed to download session PDF ZIP',
      responseType: 'blob',
      skipUnauthorizedHandler: true,
    }
  );
}
